import MenuTop from "../../../components/menu/top/menu-top";
import { useParams } from "react-router-dom";
import { fetchEditProjectService, fetchProjectService } from "../../../features/project/service/project-service";
import { useCallback, useEffect, useState } from "react";
import { fetchFinanciersService } from "../../../features/financial/service/financiador-service";
import { fetchUserCompanyService, fetchCompaniesService as getCompanies } from "../../../features/company/service/company";
import { useNavigate } from "react-router-dom";
import { projectData } from "../../../features/project/forms/project-data";
import { useAlert } from "../../../components/alert/alert";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { Project } from "../../../shared/interfaces/project";
import { FormProps, FormTypes } from "../../../shared/interfaces/form";
import Form from "../../../components/form/form";
import storage from "../../../shared/services/storage";
import { getFinancialsByProjectIdService } from "../../../features/financial/service/financeiro-service";
import FinanceTimeline from "../../../components/timeline/timeline";
import InfoComponent from "../../../components/info/info";
import { fetchSearchersListService } from "../../../features/user/service/user-service";

function ProjectEdit() {
    const { id } = useParams();
    const [ formData, setFormData ] = useState<FormProps[] | any>();
    const [ formSections, setFormSections ] = useState<any[]>();
    const { showAlert } = useAlert();
    const [ timelineData, setTimelineData ] = useState<any[]>();
    const [ company, setCompany ] = useState<any>();
    const [ isLoading, setIsLoading ] = useState(true);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const navigate = useNavigate();

    const fetchSearchers = useCallback(async () => {
        try {
            const response = await fetchSearchersListService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching searchers:", error);
            return [];
        }
    }, []);

    const fetchFinanciers = useCallback(async () => {
        try {
            const response = await fetchFinanciersService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching financiers:", error);
            return [];
        }
    }, []);

    const fetchListCompanies = useCallback(async () => {
        try {
            const response = await getCompanies();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching companies:", error);
            return [];
        }
    }, []);

    const fetchUserCompany = useCallback(async () => {
        try {
            const userLogged = await storage.getStorage('user');
            if (!userLogged) return null;
            return await fetchUserCompanyService(JSON.parse(userLogged).company.id);
        } catch (error) {
            console.error("Error fetching user company:", error);
            return null;
        }
    }, []);

    const organizeFormDataIntoSections = (data: FormProps[]) => {
        const projectFields = ['name', 'principalInvestigatorId', 'initialDate', 'finalDate', 'duration'];
        const financialFields = ['financierId', 'accountabilittyDate', 'valor', 'multiplePayments', 'multiplePaymentsRecurrency'];
        const clientFields = ['companyId'];

        const projectData = data.filter(field => projectFields.includes(field.name));
        const financialData = data.filter(field => financialFields.includes(field.name));
        const clientData = data.filter(field => clientFields.includes(field.name));

        return [
            {
                title: "Informações do Projeto",
                icon: "📋",
                color: "project",
                fields: projectData
            },
            {
                title: "Informações Financeiras", 
                icon: "💰",
                color: "financial",
                fields: financialData
            },
            {
                title: "Informações do Cliente",
                icon: "🏢", 
                color: "client",
                fields: clientData
            }
        ];
    };

    const fetchAllData = useCallback(async (projectId: string) => {
        setIsLoading(true);
        try {
            const response = await fetchProjectService(projectId);
            const { principalInvestigator, financier, initialDate, finalDate, companyId, ...restResponse } = response;
            
            restResponse.rubricasWithValues = response.rubricasWithValues;
            
            const [searchers, financiers, companies, userCompany, financialTimeline] = await Promise.all([
                fetchSearchers(),
                fetchFinanciers(),
                fetchListCompanies(),
                fetchUserCompany(),
                getFinancialsByProjectIdService(projectId)
            ]);

            setCompany(userCompany);
            setTimelineData(financialTimeline);

            const hasMultiplePaymentsRecurrency = response.multiplePaymentsRecurrency != null && response.multiplePaymentsRecurrency !== '';
            if (hasMultiplePaymentsRecurrency) {
                restResponse.multiplePayments = true;
            }

            const data = projectData(
                projectId, 
                restResponse, 
                principalInvestigator || {}, 
                searchers || [], 
                financiers || [], 
                initialDate, 
                finalDate,
                financier,
                companyId,
                companies,
                userCompany?.name,
                financialTimeline
            );

            const filteredData = data.filter(field => 
                field.name !== 'movements' && 
                field.name !== 'balance'
            );

            if (hasMultiplePaymentsRecurrency) {
                const checkboxIndex = filteredData.findIndex(item => item.name === 'multiplePayments');
                if (checkboxIndex !== -1) {
                    const newField = {
                        typeOfField: FormTypes.INPUT,
                        type: 'number',
                        placeholder: 'Recorrência dos pagamentos (em meses)*',
                        rules: {
                            required: 'Recorrência dos pagamentos é obrigatória',
                            min: {
                                value: 1,
                                message: 'A recorrência deve ser pelo menos 1 mês'
                            }
                        },
                        value: response.multiplePaymentsRecurrency,
                        name: 'multiplePaymentsRecurrency',
                        width: 200,
                        height: 55,
                        disabled: false
                    };
                    
                    filteredData.splice(checkboxIndex + 1, 0, newField);
                }
            }

            if (response.rubricasWithValues && response.rubricasWithValues.length > 0) {
                filteredData.push({
                    typeOfField: FormTypes.HIDDEN,
                    name: 'rubricasWithValues',
                    placeholder: 'Rubricas',
                    value: response.rubricasWithValues
                });
            }

            setFormData(filteredData);
            
            const sections = organizeFormDataIntoSections(filteredData);
            setFormSections(sections);

        } catch (error) {
            console.error("Error fetching project data:", error);
            showAlert('Erro ao carregar dados do projeto', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [fetchSearchers, fetchFinanciers, fetchListCompanies, fetchUserCompany, showAlert]);

    const handleFieldChange = useCallback((fieldName: string, value: any) => {
        setFormData((prevFormData: FormProps[]) => {
            if (!prevFormData || !prevFormData.length) return prevFormData;
            
            return prevFormData.map(item => {
                if (item.name === fieldName) {
                    return { ...item, value: value };
                }
                return item;
            });
        });
    }, []);

    const handleCheckboxChange = useCallback((value: boolean, fieldName: string) => {
        if (fieldName === 'multiplePayments') {
            setFormData((prevFormData: FormProps[]) => {
                if (!prevFormData || !prevFormData.length) return prevFormData;
                const updatedFormData = prevFormData.map(item => {
                    if (item.name === 'multiplePayments') {
                        return { ...item, value: value };
                    }
                    return item;
                });
                const checkboxIndex = updatedFormData.findIndex(item => item.name === 'multiplePayments');
                let finalFormData;
                if (value) {
                    const fieldExists = updatedFormData.some((item: FormProps) => item.name === 'multiplePaymentsRecurrency');
                    if (!fieldExists && checkboxIndex !== -1) {
                        const newField: FormProps = {
                            typeOfField: FormTypes.INPUT,
                            type: 'number',
                            placeholder: 'Recorrência dos pagamentos (em meses)*',
                            rules: {
                                required: 'Recorrência dos pagamentos é obrigatória',
                                min: { value: 1, message: 'A recorrência deve ser pelo menos 1 mês' }
                            },
                            value: null,
                            name: 'multiplePaymentsRecurrency',
                            width: 200,
                            height: 55,
                            disabled: false
                        };
                        const insertIndex = checkboxIndex + 1;
                        finalFormData = [
                            ...updatedFormData.slice(0, insertIndex),
                            newField,
                            ...updatedFormData.slice(insertIndex)
                        ];
                    } else {
                        finalFormData = updatedFormData;
                    }
                } else {
                    finalFormData = updatedFormData.filter(item => item.name !== 'multiplePaymentsRecurrency');
                }
                const sections = organizeFormDataIntoSections(finalFormData);
                setFormSections(sections);
                return finalFormData;
            });
        }
    }, []);

    // 🔥 Novo: callback para sincronizar rubricas com o Form
    const handleRubricasUpdate = useCallback((rubricas: any[]) => {
        setFormData((prev: FormProps[]) => {
            if (!prev) return prev;
            const exists = prev.some(f => f.name === 'rubricasWithValues');

            if (!exists) {
                return [
                    ...prev,
                    { typeOfField: FormTypes.HIDDEN, name: 'rubricasWithValues', value: rubricas }
                ];
            }
            return prev.map(f => f.name === 'rubricasWithValues' ? { ...f, value: rubricas } : f);
        });
    }, []);

    const onSubmitForm = async (data: Project) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setIsLoading(true);
        try {
            data.subProject = null;
            if (company?.name?.toLocaleLowerCase() !== 'causale') {
                data.companyId = company.id;
            }
            if (!data.multiplePayments) {
                data.multiplePaymentsRecurrency = 0;
            }

            const rubricasField = formData?.find((f: FormProps) => f.name === 'rubricasWithValues');
            if (rubricasField?.value?.length) {
                data.rubricasWithValues = rubricasField.value;
            }

            await fetchEditProjectService(id as string, data);
            showAlert('Projeto editado com sucesso', SEVERITY.SUCCESS);
        } catch (error: any) {
            showAlert(error.message || 'Erro ao editar projeto', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        if (id) { fetchAllData(id) }
    }, [id, fetchAllData]);

    return (
        <div className="container-show-project">
            <MenuTop />
            <InfoComponent />
            <div className="container-show-project__container">
                <Form 
                    data={formData}
                    sections={formSections}
                    title='Editar projeto'
                    onSubmitForm={onSubmitForm} 
                    onCancel={() => navigate('/dashboard', {replace: true})} 
                    isLoading={isLoading}
                    showFinancial={true}
                    onChangeCheckbox={handleCheckboxChange}
                    onFieldValueChange={handleFieldChange}
                    onRubricasUpdate={handleRubricasUpdate}
                    onClickedMenuContextType={(type: string) => {
                        if (type === 'financial') {
                            navigate(`/financeiro/${id}/adicionar-projeto`, {replace: true});
                        }
                    }}
                />
                <FinanceTimeline data={timelineData as []}/>
            </div>
        </div>
    );
}

export default ProjectEdit;
